import React from "react";
import { Auth0Provider } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";

/**
 * Auth0 provider wrapper.
 *
 * Strict P0 rules enforced here:
 *   • redirectUri  — derived from window.location.origin at RUNTIME.
 *                    NEVER REACT_APP_BACKEND_URL or any hardcoded URL.
 *   • domain/clientId/audience — env vars only.
 *   • returnTo on logout — also from window.location.origin.
 *
 * The Auth0 dashboard has the matching origin in its allowlist for every
 * environment we deploy to (production answerwatch.com, www, preview,
 * localhost). See /app/memory/AUTH0_SETUP.md.
 */
export default function Auth0ProviderWithRouter({ children }) {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

  const navigate = useNavigate();

  if (!domain || !clientId || !audience) {
    // Dev / preview before Auth0 env vars are set — render children unauthenticated
    // so the marketing pages still work. Calls into Auth0 will fail fast.
    if (typeof window !== "undefined" && window.console) {
      // eslint-disable-next-line no-console
      console.warn(
        "[Auth0] REACT_APP_AUTH0_DOMAIN / CLIENT_ID / AUDIENCE not set — auth disabled."
      );
    }
    return <>{children}</>;
  }

  // After Auth0 redirects back to /callback, this hands control to the SPA
  // router so we land on the page the user wanted (or /dashboard).
  const onRedirectCallback = (appState) => {
    const dest =
      (appState && appState.returnTo) ||
      window.sessionStorage.getItem("auth_return_to") ||
      "/dashboard";
    window.sessionStorage.removeItem("auth_return_to");
    navigate(dest, { replace: true });
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        // RUNTIME origin — never an env var. Production: answerwatch.com,
        // preview: the platform's preview host, dev: localhost.
        redirect_uri: window.location.origin + "/callback",
        audience: audience,
        scope: "openid profile email offline_access",
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}
